import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/common/PageHeader';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useBeatRunRefresh from '../components/news-beats/useBeatRunRefresh';
// Shared with the Beats home: one cadence described two ways is a thing nobody
// notices until a customer asks which is right.
import { cadenceLabel } from './cadence';
import type { NewsBeatSummary } from '../types';

/**
 * The News Beats list.
 *
 * A beat is a standing news watch; this page is the shelf they sit on. Filtering
 * is client-side, the same call the brainstorm list makes: a user has a handful
 * of beats, so a round trip per keystroke would be slower than the filtering it
 * replaces.
 */

type SortKey = 'recent' | 'name';


const STATUS_STYLE: Record<string, string> = {
  QUEUED: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  RUNNING: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 animate-pulse',
  COMPLETED: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  FAILED: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  CANCELLED: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
};

function relative(iso?: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  const mins = Math.max(Math.round((Date.now() - then) / 60000), 0);
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function NewsBeats() {
  const navigate = useNavigate();
  const [beats, setBeats] = useState<NewsBeatSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gnewsConfigured, setGnewsConfigured] = useState(true);

  const [query, setQuery] = useState('');
  const [onlyActive, setOnlyActive] = useState<boolean | null>(null);
  const [sort, setSort] = useState<SortKey>('recent');

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rows, status] = await Promise.all([
        api.listNewsBeats(),
        api.getGNewsStatus().catch(() => ({ configured: true })),
      ]);
      setBeats(rows);
      setGnewsConfigured(status.configured);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  // A run starting or ending moves a card's status, story count and tracked count.
  useBeatRunRefresh(load);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const beat = await api.createNewsBeat({
        name: newName.trim() || 'Untitled beat',
        // A new beat starts INACTIVE. It has no keywords yet, so the scheduler
        // would pick it up and produce an empty briefing before the user had a
        // chance to finish setting it up.
        is_active: false,
      });
      navigate(`/news-beats/${beat.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  // Counts over ALL beats, not the filtered set — the chips keep describing the
  // whole list while you narrow it.
  const activeCount = beats.filter((b) => b.is_active).length;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = beats
      .filter((b) => onlyActive === null || b.is_active === onlyActive)
      .filter(
        (b) =>
          !needle ||
          b.name.toLowerCase().includes(needle) ||
          (b.description ?? '').toLowerCase().includes(needle),
      );
    return sort === 'name'
      ? [...filtered].sort((a, b) => a.name.localeCompare(b.name))
      : filtered;
  }, [beats, query, onlyActive, sort]);

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Loading…</div>;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="News Beats"
        subtitle="A beat watches the news on your terms and turns each run into a short briefing - one entry per story, with every source that reported it."
        className=""
        actions={
          <button
            onClick={() => setCreating((v) => !v)}
            className="shrink-0 px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            {creating ? 'Cancel' : 'New beat'}
          </button>
        }
      />

      {!gnewsConfigured && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          No GNews API key is configured, so beats cannot fetch articles. An
          admin can add one under <strong>Settings → Connections → News API</strong>.
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {creating && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            What should this beat watch?
          </label>
          <div className="flex gap-2">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
              placeholder="e.g. Mountain West Energy"
              className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm"
            />
            <button
              onClick={create}
              disabled={saving}
              className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            You'll add keywords and filters next. The beat stays switched off
            until you turn it on.
          </p>
        </div>
      )}

      {beats.length > 5 && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or description…"
            className="flex-1 min-w-[12rem] border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-1.5 text-sm"
          />
          <button
            onClick={() => setOnlyActive(onlyActive === true ? null : true)}
            className={`px-2.5 py-1 text-xs rounded-full border ${
              onlyActive === true
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            Active ({activeCount})
          </button>
          <button
            onClick={() => setOnlyActive(onlyActive === false ? null : false)}
            className={`px-2.5 py-1 text-xs rounded-full border ${
              onlyActive === false
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            Paused ({beats.length - activeCount})
          </button>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm"
          >
            <option value="recent">Newest first</option>
            <option value="name">By name</option>
          </select>
        </div>
      )}

      {beats.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No beats yet. Create one to start watching a subject.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No beats match those filters.
          </p>
          <button
            onClick={() => {
              setQuery('');
              setOnlyActive(null);
            }}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((beat) => (
            <Link
              key={beat.id}
              to={`/news-beats/${beat.id}`}
              className="block rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-indigo-400 dark:hover:border-indigo-500 transition"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                  {beat.name}
                </h2>
                <span
                  className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${
                    beat.is_active
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {beat.is_active ? 'Active' : 'Paused'}
                </span>
              </div>

              {beat.description && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                  {beat.description}
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {cadenceLabel(beat.interval_minutes)}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {beat.keyword_count} terms
                </span>
                {beat.attribute_count > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {beat.attribute_count} attributes
                  </span>
                )}
                {beat.project_name && (
                  <span className="px-2 py-0.5 rounded-full bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300">
                    {beat.project_name}
                  </span>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {beat.last_briefing_status ? (
                    <span
                      className={`px-1.5 py-0.5 rounded ${
                        STATUS_STYLE[beat.last_briefing_status] ?? ''
                      }`}
                    >
                      {beat.last_briefing_status.toLowerCase()}
                    </span>
                  ) : (
                    'never run'
                  )}
                  {beat.last_briefing_at && ` · ${relative(beat.last_briefing_at)}`}
                </span>
                <span>
                  {beat.last_briefing_stories > 0 &&
                    `${beat.last_briefing_stories} stories`}
                  {beat.active_thread_count > 0 &&
                    ` · ${beat.active_thread_count} tracked`}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
